import { CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  IconButton,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import {
  deadlineSubtitle,
  dismissNudge,
  formatCount,
  formatMinutesShort,
  formatShortDate,
  projectedFinishLabel,
  shouldShowNudge,
} from "../../helpers/goals";
import GoalCard from "./GoalCard";

const Figure = ({ value, label }) => (
  <Box>
    <Text fontSize="xl" fontWeight="bold" lineHeight="1.1">
      {value}
    </Text>
    <Text color="gray.400" fontSize="sm">
      {label}
    </Text>
  </Box>
);

/**
 * The most important element of the feature is the pace marker: the tick
 * shows where the user should be, the fill shows where they are.
 */
const DeadlineGoalCard = ({ goal, onManage, onMoveDate, onUpdateTarget }) => {
  const progress = goal.progress || {};
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const isExpired =
    goal.status === "expired" || progress.paceStatus === "expired";
  const isPaused = goal.status === "paused";
  const percent = Math.min(Math.max(Number(progress.percent) || 0, 0), 100);
  const expectedPercent =
    progress.expectedPercent !== null && progress.expectedPercent !== undefined
      ? Math.min(Math.max(Number(progress.expectedPercent), 0), 100)
      : null;
  const showNudge =
    !isPaused &&
    !isExpired &&
    !!progress.nudgeDate &&
    !nudgeDismissed &&
    shouldShowNudge(goal.id);
  const hasRequirement =
    progress.requiredPerDay !== null && progress.requiredPerDay !== undefined;

  return (
    <GoalCard goal={goal} subtitle={deadlineSubtitle(goal)} onManage={onManage}>
      <Box mt="4" position="relative">
        <Box bg="#2E3A59" borderRadius="full" h="6px" overflow="hidden">
          <Box bg="red.500" h="100%" w={`${percent}%`} borderRadius="full" />
        </Box>
        {expectedPercent !== null && !isPaused && (
          <Box
            aria-label={`On pace: ${progress.expected}`}
            bg="gray.100"
            borderRadius="1px"
            h="14px"
            left={`calc(${expectedPercent}% - 1px)`}
            position="absolute"
            title={`On pace: ${progress.expected}`}
            top="-4px"
            w="2px"
          />
        )}
      </Box>
      <Flex justify="space-between" mt="2">
        <Text color="gray.400" fontSize="sm">
          {progress.completed} of {progress.target} read
        </Text>
        {expectedPercent !== null && !isPaused && (
          <Text color="gray.400" fontSize="sm">
            on pace: {progress.expected}
          </Text>
        )}
      </Flex>

      {!isPaused && !isExpired && hasRequirement && (
        <>
          <Divider borderColor="#30384E" my="4" />
          <Grid templateColumns="repeat(3, 1fr)" gap="3">
            <Figure
              value={`${formatCount(progress.requiredPerDay)}/day`}
              label="needed now"
            />
            <Figure
              value={formatMinutesShort(progress.requiredMinutesPerDay)}
              label="a day"
            />
            <Figure
              value={projectedFinishLabel(progress)}
              label={
                progress.projectedFinish ? "at your pace" : "no recent reading"
              }
            />
          </Grid>
        </>
      )}

      {isExpired && (
        <Flex gap="2" mt="4" wrap="wrap">
          <Text color="gray.300" fontSize="sm" w="100%">
            Deadline passed. Extend, or keep reading without one?
          </Text>
          <Button
            colorScheme="red"
            onClick={() => onMoveDate?.(goal, progress.suggestedEndDate)}
            size="sm"
          >
            Extend
          </Button>
          <Button
            onClick={() => onMoveDate?.(goal, null)}
            size="sm"
            variant="outline"
          >
            Keep reading without a deadline
          </Button>
        </Flex>
      )}

      {showNudge && (
        <Flex
          align="center"
          bg="#1A2138"
          borderRadius="lg"
          gap="2"
          mt="4"
          px="3"
          py="2"
        >
          <Text color="gray.200" fontSize="sm" flex="1">
            Move to {formatShortDate(progress.nudgeDate)}?
          </Text>
          <Button
            colorScheme="red"
            onClick={() => onMoveDate?.(goal, progress.nudgeDate)}
            size="xs"
            variant="outline"
          >
            Move the date
          </Button>
          <IconButton
            aria-label="Dismiss suggestion"
            icon={<CloseIcon boxSize="2.5" />}
            onClick={() => {
              dismissNudge(goal.id);
              setNudgeDismissed(true);
            }}
            size="xs"
            variant="ghost"
          />
        </Flex>
      )}

      {Number(progress.orderGrewBy) > 0 && !isPaused && (
        <Flex
          align="center"
          bg="#1A2138"
          borderRadius="lg"
          gap="2"
          mt="4"
          px="3"
          py="2"
        >
          <Text color="gray.200" fontSize="sm" flex="1">
            {goal.readingOrder?.title || "This order"} now has{" "}
            {progress.orderGrewBy} more{" "}
            {progress.orderGrewBy === 1 ? "issue" : "issues"}. Update your goal?
          </Text>
          <Button
            colorScheme="red"
            onClick={() => onUpdateTarget?.(goal, progress.orderTotal)}
            size="xs"
            variant="outline"
          >
            Update
          </Button>
        </Flex>
      )}
    </GoalCard>
  );
};

export default DeadlineGoalCard;
